import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  Users,
  Boxes,
  Sparkles,
  Receipt,
  LineChart,
  Building2,
} from "lucide-react";
import { SidebarWorkspaceMenu } from "./SidebarWorkspaceMenu";
import { SidebarUserMenu } from "./SidebarUserMenu";

/**
 * 240 px-wide sidebar. Layout follows the enterprise pattern shared
 * by Slack / Notion / Linear / GitHub:
 *
 *   ┌──────────────────────────────┐
 *   │ Workspace block (top)        │  ← SidebarWorkspaceMenu
 *   │   logo · name · role · ▾     │
 *   ├──────────────────────────────┤
 *   │ Main         Dashboard, …    │
 *   │ Management   Services, …     │
 *   ├──────────────────────────────┤
 *   │ User block (bottom)          │  ← SidebarUserMenu
 *   │   avatar · name · email · ▾  │
 *   └──────────────────────────────┘
 *
 * Workspace context lives at the top because every nav item below
 * is scoped by it. User identity sits at the bottom since it's
 * secondary to the workspace lens.
 */

const MAIN = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/bookings", label: "Bookings", icon: ClipboardList },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/inventory", label: "Inventory", icon: Boxes },
];

const MANAGEMENT = [
  { to: "/services", label: "Services", icon: Sparkles },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/insights", label: "Insights", icon: LineChart },
  { to: "/workspace", label: "Workspace", icon: Building2 },
];

export function Sidebar() {
  return (
    <aside className="bg-sidebar border-r border-sidebar-border flex flex-col h-screen sticky top-0">
      <div className="px-2 py-2 border-b border-sidebar-border">
        <SidebarWorkspaceMenu />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        <Group label="Main" items={MAIN} />
        <Group label="Management" items={MANAGEMENT} />
      </nav>

      <div className="px-2 py-2 border-t border-sidebar-border">
        <SidebarUserMenu />
      </div>
    </aside>
  );
}

function Group({
  label,
  items,
}: {
  label: string;
  items: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; end?: boolean }[];
}) {
  return (
    <div>
      <p className="px-3 mb-2 text-[11px] uppercase tracking-wider text-sidebar-muted">{label}</p>
      <ul className="space-y-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.to}>
              <NavLink
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{it.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
